import React, { useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import { isRTL } from "@/i18n";
import LandingPage from "@/pages/LandingPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AdminPage from "@/pages/AdminPage";
import AssistantTestPage from "@/pages/AssistantTestPage";
import AssistantWidget from "@/components/AssistantWidget";

import TrustSafetyPage from "@/pages/TrustSafetyPage";
import PrivacyPage from "@/pages/PrivacyPage";
import PilotProgramPage from "@/pages/PilotProgramPage";
import ForClinicsPage from "@/pages/ForClinicsPage";
import ForEducationPage from "@/pages/ForEducationPage";
import ForCompaniesPage from "@/pages/ForCompaniesPage";
import ForIndividualsPage from "@/pages/ForIndividualsPage";

import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminContactRequestsPage from "@/pages/admin/AdminContactRequestsPage";
import AdminContactDetailPage from "@/pages/admin/AdminContactDetailPage";
import AdminEmailLogsPage from "@/pages/admin/AdminEmailLogsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminAssistantLogsPage from "@/pages/admin/AdminAssistantLogsPage";
import AdminContentManagerPage from "@/pages/admin/AdminContentManagerPage";

import AdminCrmContactsPage from "@/pages/admin/crm/AdminCrmContactsPage";
import AdminCrmContactDetailPage from "@/pages/admin/crm/AdminCrmContactDetailPage";
import AdminCrmContactCreatePage from "@/pages/admin/crm/AdminCrmContactCreatePage";
import AdminCrmTasksPage from "@/pages/admin/crm/AdminCrmTasksPage";
import AdminCrmPipelinePage from "@/pages/admin/crm/AdminCrmPipelinePage";
import AdminCrmReportsPage from "@/pages/admin/crm/AdminCrmReportsPage";
import AdminCrmOutreachInboxPage from "@/pages/admin/crm/AdminCrmOutreachInboxPage";
import { TrackerProvider } from "@/lib/tracker";

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = isRTL(i18n.language) ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TrackerProvider>
            <Routes>
              {/* Public site */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/insights" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />

              {/* Secondary public pages */}
              <Route path="/trust-safety" element={<TrustSafetyPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/pilot-program" element={<PilotProgramPage />} />
              <Route path="/for-clinics" element={<ForClinicsPage />} />
              <Route path="/for-education" element={<ForEducationPage />} />
              <Route path="/for-companies" element={<ForCompaniesPage />} />
              <Route path="/for-individuals" element={<ForIndividualsPage />} />

              {/* Admin auth */}
              <Route path="/admin/login" element={<AdminLoginPage />} />

              {/* Admin (protected) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminOverviewPage />} />
                <Route path="contact-requests" element={<AdminContactRequestsPage />} />
                <Route path="contact-requests/:id" element={<AdminContactDetailPage />} />
                <Route path="emails" element={<AdminEmailLogsPage />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="assistant-logs" element={<AdminAssistantLogsPage />} />
                <Route path="content" element={<AdminContentManagerPage />} />
                <Route path="crm/contacts" element={<AdminCrmContactsPage />} />
                <Route path="crm/contacts/new" element={<AdminCrmContactCreatePage />} />
                <Route path="crm/contacts/:id" element={<AdminCrmContactDetailPage />} />
                <Route path="crm/pipeline" element={<AdminCrmPipelinePage />} />
                <Route path="crm/tasks" element={<AdminCrmTasksPage />} />
                <Route path="crm/reports" element={<AdminCrmReportsPage />} />
                <Route path="crm/outreach-inbox" element={<AdminCrmOutreachInboxPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="blog" element={<AdminPage />} />
                <Route path="assistant" element={<AssistantTestPage />} />
              </Route>
            </Routes>
            <AssistantWidget />
          </TrackerProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
      <div className="noise-overlay" aria-hidden="true" />
    </div>
  );
}

export default App;
